import React, { useState } from "react";
import { X, Bookmark, Plus, Tag } from "lucide-react";
import { useSaveThread, useCollections } from "../../../services/communities";
import "./SaveThreadModal.css";

/**
 * Modal for saving threads to collections with tags
 */
const SaveThreadModal = ({ thread, onClose }) => {
  const [selectedCollection, setSelectedCollection] = useState("");
  const [newCollectionName, setNewCollectionName] = useState("");
  const [tags, setTags] = useState("");
  const [showNewCollection, setShowNewCollection] = useState(false);

  const { data: collectionsData, isLoading: collectionsLoading } =
    useCollections();
  const saveThreadMutation = useSaveThread();

  const collections = collectionsData?.collections || [];

  const handleSave = async () => {
    const collectionToSave = showNewCollection
      ? newCollectionName.trim()
      : selectedCollection;
    const tagsArray = tags
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);

    try {
      await saveThreadMutation.mutateAsync({
        threadId: thread._id,
        collection: collectionToSave || null,
        tags: tagsArray,
      });
      onClose();
    } catch (error) { /* ignored */ }
  };

  const handleQuickSave = async () => {
    try {
      await saveThreadMutation.mutateAsync({
        threadId: thread._id,
        collection: null, // Default collection
        tags: [],
      });
      onClose();
    } catch (error) { /* ignored */ }
  };

  return (
    <div className="save-thread-modal-overlay" onClick={onClose}>
      <div className="save-thread-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="save-modal-header">
          <div className="save-modal-title">
            <Bookmark size={20} />
            <h3>Save Thread</h3>
          </div>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Thread Preview */}
        <div className="save-modal-thread-preview">
          <h4>{thread.title}</h4>
          {thread.description && (
            <p>{thread.description.substring(0, 150)}...</p>
          )}
        </div>

        {/* Collection Selector */}
        <div className="save-modal-section">
          <label className="save-modal-label">
            <Bookmark size={16} />
            Collection
          </label>

          {!showNewCollection ? (
            <>
              <select
                value={selectedCollection}
                onChange={(e) => setSelectedCollection(e.target.value)}
                className="save-modal-select"
                disabled={collectionsLoading}
              >
                <option value="">Default Collection</option>
                {collections.map((collection) => (
                  <option key={collection.name} value={collection.name}>
                    {collection.name} ({collection.count})
                  </option>
                ))}
              </select>

              <button
                className="create-collection-btn"
                onClick={() => setShowNewCollection(true)}
              >
                <Plus size={14} />
                Create New Collection
              </button>
            </>
          ) : (
            <>
              <input
                type="text"
                value={newCollectionName}
                onChange={(e) => setNewCollectionName(e.target.value)}
                placeholder="Enter collection name"
                className="save-modal-input"
                autoFocus
              />
              <button
                className="cancel-new-collection-btn"
                onClick={() => {
                  setShowNewCollection(false);
                  setNewCollectionName("");
                }}
              >
                Cancel
              </button>
            </>
          )}
        </div>

        {/* Tags Input */}
        <div className="save-modal-section">
          <label className="save-modal-label">
            <Tag size={16} />
            Tags (optional)
          </label>
          <input
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="e.g., important, reference, research"
            className="save-modal-input"
          />
          <span className="save-modal-hint">Separate tags with commas</span>
        </div>

        {/* Actions */}
        <div className="save-modal-actions">
          <button
            className="quick-save-btn"
            onClick={handleQuickSave}
            disabled={saveThreadMutation.isPending}
          >
            Quick Save
          </button>
          <button
            className="save-detailed-btn"
            onClick={handleSave}
            disabled={
              saveThreadMutation.isPending ||
              (showNewCollection && !newCollectionName.trim())
            }
          >
            {saveThreadMutation.isPending ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveThreadModal;
